import React, {useContext, useEffect, useMemo, useState} from "react"
import {Alert, Box, Button, Chip, CircularProgress, IconButton, Link, Stack, Tooltip, Typography} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import RefreshIcon from "@mui/icons-material/Refresh"
import AutorenewIcon from "@mui/icons-material/Autorenew"
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined"
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined"
import {
    useDeleteAiMessageDraftsMutation,
    useDeleteAiTicketDraftsMutation,
    useGetAiDialogQuery,
    useGetAiTicketKnowledgeCandidatesQuery,
    useRegenerateAiAnswerMutation
} from "../../core/api/ai-zendesk-api"
import {SnackBarContext} from "../../snackBarProvider"
import {
    buildAnswerContext,
    buildTimeline,
    CRITICAL_CATEGORIES,
    decisionLabel,
    DIALOG_STATUS_COLOR,
    DIALOG_STATUS_LABEL,
    errorText,
    formatExact,
    formatTime,
    gameLabel,
    isPendingAnswer,
    pendingSourceCount,
    zendeskTicketUrl
} from "./aiTicketsUtils"
import AnswerCard from "./AnswerCard"
import {ConfirmDialog} from "./DecisionDialogs"
import {useCreateAiKnowledgeNoteMutation} from "../../core/api/ai-zendesk-settings-api"
import {CandidateCard, NoteDialog} from "../../containers/AiZendesk/KnowledgeEditor"

/**
 * Knowledge from this ticket: candidates the quality judge extracted from the operator's reply (approve / reject
 * right here) and a shortcut to write a knowledge note by hand with this ticket as the source.
 */
const TicketKnowledge = ({ticketId, gameTag}) => {
    const {addAlert} = useContext(SnackBarContext)
    const [open, setOpen] = useState(false)
    const [adding, setAdding] = useState(false)
    const {data: candidates = []} = useGetAiTicketKnowledgeCandidatesQuery(ticketId)
    const [createNote, {isLoading: creating}] = useCreateAiKnowledgeNoteMutation()
    const pending = candidates.filter((c) => c.status === "NEW").length

    const onCreate = async ({title, body}) => {
        try {
            await createNote({gameTag, title, body, ticketId}).unwrap()
            addAlert({text: "Knowledge note created", type: "success"})
            setAdding(false)
        } catch (error) {
            addAlert({text: errorText(error), type: "error"})
        }
    }

    return (
        <Box sx={{px: 1, pb: 1}}>
            <Stack direction="row" spacing={0.5} sx={{alignItems: "center"}}>
                <Button size="small" variant="text" startIcon={<SchoolOutlinedIcon fontSize="small"/>} onClick={() => setOpen((v) => !v)} sx={{fontSize: 12}}>
                    Knowledge from this ticket{candidates.length ? ` (${pending} to review, ${candidates.length} total)` : ""}
                </Button>
                <Box sx={{flex: 1}}/>
                <Tooltip title={gameTag ? "Write a general game rule learned from this ticket into the bot's knowledge base" : "The dialog has no game tag"}>
                    <span>
                        <Button size="small" variant="outlined" disabled={!gameTag} onClick={() => setAdding(true)} sx={{fontSize: 12}}>
                            Add knowledge note
                        </Button>
                    </span>
                </Tooltip>
            </Stack>
            {open && (
                <Stack spacing={1} sx={{mt: 1}}>
                    {candidates.length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                            No candidates: they appear after the ticket is solved with an operator reply and reviewed by the quality judge.
                        </Typography>
                    )}
                    {candidates.map((candidate) => <CandidateCard key={candidate.id} candidate={candidate} compact/>)}
                </Stack>
            )}
            <NoteDialog
                open={adding}
                title={`Knowledge note from ticket #${ticketId}`}
                hint="Write the general rule the operator relied on, not this player's account state. Saved as a staff-only Help Center article the bot uses; editable later in Zendesk Guide."
                initial={{gameTag}}
                loading={creating}
                confirmText="Create"
                onClose={() => setAdding(false)}
                onSave={onCreate}
            />
        </Box>
    )
}

const DIALOG_POLL_MS = 15000

const ROLE_LABEL = {USER: "Player wrote", BOT: "Bot sent to player", AGENT: "Agent (human) replied", SYSTEM: "System"}

// pipeline status of a player message, in operator words
const MESSAGE_STATUS_LABEL = {
    RECEIVED: "processing",
    ANALYZED: "processing",
    ANSWER_PREPARED: "answer prepared",
    ANSWERED: "answered",
    ESCALATED: "escalated",
    HANDLED_BY_RULE: "handled by rule",
    SKIPPED_DUPLICATE: "skipped: duplicate",
    SKIPPED_SUPERSEDED: "skipped: superseded",
    SKIPPED_DISABLED: "skipped: bot disabled",
    SKIPPED_NON_USER: "",
    SKIPPED_AGENT_OWNED: "skipped: agent owns ticket",
    SKIPPED_SERVICE: "skipped: service message",
    SILENCED: "bot stays silent",
    DRAFTS_DELETED: "drafts deleted — regenerate to get a new one",
    FAILED: "analysis failed"
}

const messageMeta = (message) => {
    const parts = []
    if (message.messageType) parts.push(message.messageType.toLowerCase().replace("_", " "))
    if (message.category) parts.push(message.category)
    const status = MESSAGE_STATUS_LABEL[message.status] ?? message.status?.toLowerCase()
    if (status) parts.push(status)
    if (message.decision && !["ANSWER", "GREET"].includes(message.decision)) parts.push(decisionLabel(message.decision))
    if (message.status === "FAILED" && message.failReason) parts.push(message.failReason)
    return parts.join(" · ")
}

// the engine skips these anyway (service forms, messaging echo) or is still working on them
const NOT_REGENERATABLE = new Set(["RECEIVED", "ANALYZED", "SKIPPED_SERVICE", "SKIPPED_DUPLICATE"])

/** Unsent drafts per player message (sourceId) — SENT answers are never deleted */
const draftsBySource = (answers = []) => {
    const counts = {}
    for (const answer of answers) {
        if (answer.status === "SENT") continue
        const key = answer.sourceMessageId ?? answer.id
        counts[key] = (counts[key] ?? 0) + 1
    }
    return counts
}

const debugButtonSx = {fontSize: 11, textTransform: "none", py: 0, px: 0.5, minWidth: 0}

/**
 * Debug actions under a player message: regenerate the answer (unsent drafts for the message are deleted,
 * the bot answers again with the dialog context up to this message) and delete unsent drafts.
 */
const MessageDebugActions = ({message, ticketId, draftsCount}) => {
    const {addAlert} = useContext(SnackBarContext)
    const [confirm, setConfirm] = useState(false)
    const [regenerate, {isLoading: regenerating}] = useRegenerateAiAnswerMutation()
    const [deleteDrafts, {isLoading: deleting}] = useDeleteAiMessageDraftsMutation()
    if (!message.text || NOT_REGENERATABLE.has(message.status)) return null
    const busy = regenerating || deleting

    const onRegenerate = async () => {
        try {
            const answers = await regenerate({messageId: message.id, ticketId}).unwrap()
            const drafts = (answers ?? []).filter((a) => a.type === "ANSWER" && a.status === "PENDING_APPROVAL").length
            addAlert({
                text: drafts ? `New draft prepared for ticket #${ticketId}` : "Regenerated: the bot prepared no draft, see the message status",
                type: drafts ? "success" : "info"
            })
        } catch (error) {
            addAlert({text: errorText(error), type: "error"})
        }
    }

    const onDelete = async () => {
        try {
            const result = await deleteDrafts({messageId: message.id, ticketId}).unwrap()
            addAlert({text: `Deleted ${result?.deleted ?? 0} draft(s) for ticket #${ticketId}`, type: "success"})
        } catch (error) {
            addAlert({text: errorText(error), type: "error"})
        }
        setConfirm(false)
    }

    return (
        <Stack direction="row" spacing={0.5} sx={{px: 0.25}}>
            <Tooltip title="Debug: delete unsent drafts for this message and let the bot answer it again (context: the dialog up to this message)">
                <span>
                    <Button size="small" variant="text" onClick={onRegenerate} disabled={busy} sx={debugButtonSx}
                            startIcon={regenerating ? <CircularProgress size={12}/> : <AutorenewIcon sx={{fontSize: 14}}/>}>
                        {regenerating ? "Regenerating…" : "Regenerate"}
                    </Button>
                </span>
            </Tooltip>
            {draftsCount > 0 && (
                <Tooltip title="Debug: delete unsent drafts for this message from the database (sent answers stay)">
                    <span>
                        <Button size="small" variant="text" color="error" onClick={() => setConfirm(true)} disabled={busy} sx={debugButtonSx}
                                startIcon={<DeleteSweepOutlinedIcon sx={{fontSize: 14}}/>}>
                            Delete drafts ({draftsCount})
                        </Button>
                    </span>
                </Tooltip>
            )}
            <ConfirmDialog
                open={confirm}
                title="Delete drafts?"
                text={`${draftsCount} unsent draft(s) for this player message will be removed from the database. Sent answers are kept. Use Regenerate to get a new draft.`}
                confirmText="Delete"
                loading={deleting}
                onClose={() => setConfirm(false)}
                onConfirm={onDelete}
            />
        </Stack>
    )
}

const MessageBubble = ({message, ticketId, draftsCount}) => {
    const role = message.role
    const isPlayer = role === "USER"
    const colors = {
        USER: {bgcolor: "grey.200", color: "text.primary"},
        BOT: {bgcolor: "primary.main", color: "primary.contrastText"},
        AGENT: {bgcolor: "info.light", color: "info.contrastText"}
    }[role] ?? {bgcolor: "grey.100", color: "text.primary"}

    return (
        <Box sx={{display: "flex", flexDirection: "column", alignItems: isPlayer ? "flex-start" : "flex-end"}}>
            <Typography variant="caption" color="text.secondary" sx={{px: 0.5}}>
                {ROLE_LABEL[role] ?? role}
                <Tooltip title={formatExact(message.receivedAt ?? message.occurredAt)} placement="left">
                    <span> · {formatTime(message.occurredAt ?? message.receivedAt)}</span>
                </Tooltip>
            </Typography>
            <Box sx={{...colors, borderRadius: 1, px: 1.25, py: 0.75, maxWidth: "92%", whiteSpace: "pre-wrap", wordBreak: "break-word"}}>
                <Typography variant="body2" component="div">{message.text ?? ""}</Typography>
            </Box>
            {isPlayer && messageMeta(message) && (
                <Typography variant="caption" color={message.status === "FAILED" ? "error.main" : "text.secondary"} sx={{px: 0.5, fontSize: 10}}>
                    {messageMeta(message)}
                </Typography>
            )}
            {isPlayer && <MessageDebugActions message={message} ticketId={ticketId} draftsCount={draftsCount}/>}
        </Box>
    )
}

/** Zendesk triggers, forms and auto-replies — collapsed by default, they are noise for approval. */
const SystemGroup = ({items}) => {
    const [expanded, setExpanded] = useState(false)
    return (
        <Box sx={{textAlign: "center"}}>
            <Button variant="text" size="small" onClick={() => setExpanded((v) => !v)} sx={{fontSize: 11, textTransform: "none"}}>
                {expanded ? "Hide" : "Show"} {items.length} system message{items.length === 1 ? "" : "s"}
            </Button>
            {expanded && (
                <Stack spacing={0.5} sx={{textAlign: "left"}}>
                    {items.map((message) => (
                        <Typography key={message.id} variant="caption" color="text.secondary" component="div"
                                    sx={{whiteSpace: "pre-wrap", wordBreak: "break-word", px: 0.5, borderLeft: 2, borderColor: "divider"}}>
                            {formatTime(message.receivedAt)} · {message.text}
                        </Typography>
                    ))}
                </Stack>
            )}
        </Box>
    )
}

export default function TicketDialogView({ticketId, onBack}) {
    const {addAlert} = useContext(SnackBarContext)
    const [poll, setPoll] = useState(DIALOG_POLL_MS)
    const [confirmDeleteAll, setConfirmDeleteAll] = useState(false)
    const [deleteAll, {isLoading: deletingAll}] = useDeleteAiTicketDraftsMutation()
    const query = useGetAiDialogQuery(ticketId, {pollingInterval: poll})
    useEffect(() => {
        setPoll(query.isError ? 0 : DIALOG_POLL_MS)
    }, [query.isError])

    const dialog = query.data?.dialog
    const timeline = useMemo(() => buildTimeline(query.data?.messages, query.data?.answers), [query.data])
    const answerContext = useMemo(() => buildAnswerContext(query.data?.answers), [query.data])
    // greeting + main answer for one message is normal; drafts for several messages means stale ones piled up
    const pendingSources = useMemo(() => pendingSourceCount(query.data?.answers), [query.data])
    const pendingCount = useMemo(() => (query.data?.answers ?? []).filter(isPendingAnswer).length, [query.data])
    const drafts = useMemo(() => draftsBySource(query.data?.answers), [query.data])
    const draftsTotal = Object.values(drafts).reduce((sum, count) => sum + count, 0)

    const onDeleteAll = async () => {
        try {
            const result = await deleteAll({ticketId}).unwrap()
            addAlert({text: `Deleted ${result?.deleted ?? 0} draft(s) for ticket #${ticketId}`, type: "success"})
        } catch (error) {
            addAlert({text: errorText(error), type: "error"})
        }
        setConfirmDeleteAll(false)
    }

    const notFound = query.isError && query.error?.status === 404
    const critical = dialog?.category && CRITICAL_CATEGORIES.has(dialog.category)

    return (
        <>
            <Stack direction="row" spacing={0.5} sx={{alignItems: "center", px: 0.5, pt: 0.5}}>
                <IconButton onClick={onBack} aria-label="Back to ticket list"><ArrowBackIcon fontSize="small"/></IconButton>
                <Typography variant="subtitle2" sx={{flex: 1}}>
                    <Link href={zendeskTicketUrl(ticketId)} target="_blank" rel="noopener noreferrer" underline="hover"
                          sx={{display: "inline-flex", alignItems: "center", gap: 0.25}}>
                        #{ticketId}<OpenInNewIcon sx={{fontSize: 14}}/>
                    </Link>
                </Typography>
                <Tooltip title="Debug: delete all unsent drafts of this ticket (sent answers stay)">
                    <span>
                        <IconButton onClick={() => setConfirmDeleteAll(true)} disabled={draftsTotal === 0 || deletingAll} aria-label="Delete all drafts" color="error">
                            <DeleteSweepOutlinedIcon fontSize="small"/>
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Refresh">
                    <span>
                        <IconButton onClick={() => { setPoll(DIALOG_POLL_MS); query.refetch() }} disabled={query.isFetching} aria-label="Refresh">
                            {query.isFetching && !query.isLoading ? <CircularProgress size={16}/> : <RefreshIcon fontSize="small"/>}
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            {dialog && (
                <Box sx={{px: 1, pb: 1}}>
                    <Stack direction="row" spacing={0.5} sx={{flexWrap: "wrap", rowGap: 0.5}}>
                        <Chip label={DIALOG_STATUS_LABEL[dialog.status] ?? dialog.status} color={DIALOG_STATUS_COLOR[dialog.status] ?? "default"} sx={{height: 18, fontSize: 11}}/>
                        <Chip label={gameLabel(dialog.game)} variant="outlined" sx={{height: 18, fontSize: 11}}/>
                        {dialog.language && <Chip label={dialog.language} variant="outlined" sx={{height: 18, fontSize: 11}}/>}
                        {dialog.channel && <Chip label={dialog.channel.toLowerCase()} variant="outlined" sx={{height: 18, fontSize: 11}}/>}
                        {dialog.category && <Chip label={dialog.category} color={critical ? "warning" : "default"} variant="outlined" sx={{height: 18, fontSize: 11}}/>}
                        {dialog.audienceTier && <Chip label={dialog.audienceTier.replace("_", " ")} variant="outlined" sx={{height: 18, fontSize: 11}}/>}
                    </Stack>
                    {dialog.userId && (
                        <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 0.5, wordBreak: "break-all"}}>
                            user {dialog.userId}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" component="div">
                        bot turns {dialog.botTurns ?? 0} · updated {formatTime(dialog.updatedAt)}
                    </Typography>
                </Box>
            )}

            {dialog && <TicketKnowledge ticketId={ticketId} gameTag={dialog.gameTag ?? dialog.game}/>}

            {pendingSources > 1 && (
                <Alert severity="warning" sx={{mx: 1, mb: 1, py: 0, fontSize: 12}}>
                    {pendingCount} drafts for {pendingSources} player messages await your decision. The newest one has full context — reject the outdated ones.
                </Alert>
            )}

            <Box sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 1, pb: 1}}>
                {query.isLoading && <Box sx={{display: "flex", justifyContent: "center", p: 3}}><CircularProgress size={24}/></Box>}
                {notFound && (
                    <Alert severity="info" sx={{fontSize: 12}}>
                        Ticket #{ticketId} has no AI dialog: it never went through the AI flow.
                    </Alert>
                )}
                {query.isError && !notFound && (
                    <Alert severity="error" sx={{fontSize: 12}}
                           action={<Button size="small" color="inherit" variant="text" onClick={() => { setPoll(DIALOG_POLL_MS); query.refetch() }}>Retry</Button>}>
                        {errorText(query.error)}
                    </Alert>
                )}
                {query.data && timeline.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{textAlign: "center", p: 2}}>No messages yet</Typography>
                )}
                {query.data && timeline.length > 0 && !(query.data.answers ?? []).some((a) => a.type === "ANSWER") && (
                    <Alert severity="info" sx={{mb: 1, py: 0, fontSize: 12}}>
                        The bot has not prepared any answer for this ticket yet. Player messages below show why (escalated, skipped, failed).
                    </Alert>
                )}
                <Stack spacing={1.25}>
                    {timeline.map((item, index) => {
                        if (item.kind === "system-group") return <SystemGroup key={`sys-${index}`} items={item.items}/>
                        if (item.kind === "answer") return <AnswerCard key={item.data.id} answer={item.data} ticketId={ticketId} context={answerContext[item.data.id]}/>
                        return <MessageBubble key={item.data.id} message={item.data} ticketId={ticketId} draftsCount={drafts[item.data.sourceId] ?? 0}/>
                    })}
                </Stack>
            </Box>

            <ConfirmDialog
                open={confirmDeleteAll}
                title={`Delete all drafts of ticket #${ticketId}?`}
                text={`${draftsTotal} unsent draft(s) and escalation records will be removed from the database. Sent answers are kept. Use Regenerate on a player message to get a new draft.`}
                confirmText="Delete all"
                loading={deletingAll}
                onClose={() => setConfirmDeleteAll(false)}
                onConfirm={onDeleteAll}
            />
        </>
    )
}
